import { INDEX_PAGE_TYPES } from '@/components/page-editor/add-page-modal/page-template-types';
import type { FlattenedItem, TreeItems } from '@/components/ui/sortable-tree';
import http from '@/lib/http';
import { normalizePages } from '@/lib/pages';
import {
  cmsPagesChildPageSearchesPath,
  cmsPagesEditorIndexPath,
  cmsPagesEditorPath,
  cmsPagesPageTreePagesPath,
  cmsPagesPageTreeSearchPath,
} from '@/lib/routes';
import {
  DEFAULT_MAX_PINNED_PAGES,
  DEFAULT_MAX_TREE_DEPTH,
  getMaxTreeDepth,
} from '@/stores/pages-store';
import type { AccountUsage } from '@/types/account-usage';
import type { IndexPagesExist, Page, RawPageFromApi } from '@/types/page';
import { EMPTY_INDEX_PAGES_EXIST, PageType } from '@/types/page';
import type { PaginationInfo } from '@/types/pagination';
import type { SortablePageItemData } from './sortable-page-item';

export const DEFAULT_PAGE = 1;
export const DEFAULT_PER_PAGE = 20;
export const TREE_PAGE_DEFAULT_PER_PAGE = 100;

export function createDefaultPaginationInfo(overrides?: Partial<PaginationInfo>): PaginationInfo {
  return {
    currentPage: overrides?.currentPage ?? DEFAULT_PAGE,
    totalPages: overrides?.totalPages ?? 0,
    totalCount: overrides?.totalCount ?? 0,
    perPage: overrides?.perPage ?? DEFAULT_PER_PAGE,
  };
}

function normalizeIndexPagesExist(raw: unknown): IndexPagesExist {
  if (!raw || typeof raw !== 'object') return EMPTY_INDEX_PAGES_EXIST;

  const value = raw as Partial<IndexPagesExist>;
  return {
    blogIndex: Boolean(value.blogIndex),
    caseResultIndex: Boolean(value.caseResultIndex),
    reviewIndex: Boolean(value.reviewIndex),
  };
}

export const INDEX_TO_CHILD_TYPE: Record<string, string> = {
  [PageType.BLOG_INDEX]: PageType.BLOG,
  [PageType.CASE_RESULT_INDEX]: PageType.CASE_RESULT,
};

export const CHILD_TO_INDEX_TYPE: Record<string, PageType> = {
  [PageType.BLOG]: PageType.BLOG_INDEX,
  [PageType.CASE_RESULT]: PageType.CASE_RESULT_INDEX,
};

export function isIndexPageType(pageType: PageType): boolean {
  return INDEX_PAGE_TYPES.some((indexType) => indexType.key === pageType);
}

export function getIndexPageLabel(pageType: PageType): string | undefined {
  return INDEX_PAGE_TYPES.find((indexType) => indexType.key === pageType)?.label;
}

export function hasNestedPageView(pageType: PageType): boolean {
  return isIndexPageType(pageType) && pageType !== PageType.REVIEW_INDEX;
}

export function buildPagesTree(flatPages: Page[]): Page[] {
  const nodeMap = new Map<string, Page>();
  for (const page of flatPages) {
    nodeMap.set(page.id, { ...page, children: [] });
  }

  const roots: Page[] = [];
  for (const page of flatPages) {
    const node = nodeMap.get(page.id);
    if (!node) continue;

    const parentId = page.parentId;
    if (parentId && nodeMap.has(parentId)) {
      nodeMap.get(parentId)?.children?.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

export function treeItemsToPages(
  items: TreeItems<SortablePageItemData>,
  parentId: string | null = null,
): Page[] {
  return items.map((item) => ({
    ...item.page,
    parentId,
    children: item.children?.length ? treeItemsToPages(item.children, String(item.id)) : [],
  }));
}

export function flattenPagesTree(pages: Page[], parentId: string | null = null): Page[] {
  const result: Page[] = [];

  for (const page of pages) {
    const { children, ...rest } = page;
    result.push({ ...rest, parentId, children: undefined });
    if (children?.length) {
      result.push(...flattenPagesTree(children, page.id));
    }
  }

  return result;
}

export function filterPages(pages: Page[], query: string): Page[] {
  if (!query) return pages;

  const filterTree = (nodes: Page[]): Page[] =>
    nodes
      .map((page) => {
        const filteredChildren = page.children ? filterTree(page.children) : [];
        const pageTitle = (page.title || '').toLowerCase();
        const navigationTitle = (page.navigationTitle || '').toLowerCase();
        const matchesQuery = pageTitle.includes(query) || navigationTitle.includes(query);

        if (matchesQuery || filteredChildren.length > 0) {
          return { ...page, children: filteredChildren };
        }

        return null;
      })
      .filter(Boolean) as Page[];

  return filterTree(pages);
}

export function getEditorRedirectPathAfterPageDelete(
  deletedPage: Pick<Page, 'pageType' | 'indexPage'>,
  allPages: Page[],
): string {
  const indexPageType = CHILD_TO_INDEX_TYPE[deletedPage.pageType];
  if (indexPageType) {
    // First tree batch may omit the index; fall back to editor payload indexPage.
    const indexPage =
      findPageByType(allPages, indexPageType) ??
      indexPageFromEditorPage(deletedPage, indexPageType);
    if (indexPage) {
      return cmsPagesEditorPath(indexPage.id);
    }
  }

  return cmsPagesEditorIndexPath();
}

export function findPageByType(pages: Page[], pageType: PageType): Page | undefined {
  for (const page of pages) {
    if (page.pageType === pageType) {
      return page;
    }

    if (page.children?.length) {
      const foundChild = findPageByType(page.children, pageType);
      if (foundChild) {
        return foundChild;
      }
    }
  }

  return undefined;
}

export async function fetchAllPages(): Promise<{
  mainNavigationPages: Page[];
  nonNavigationPages: Page[];
  mainNavigationPagination: PaginationInfo;
  nonNavigationPagination: PaginationInfo;
  publishedPages: Page[];
  indexPagesExist: IndexPagesExist;
  maxDepth: number;
  maxPinnedPages: number;
  latestPagesUpdatedAt: string | null;
  accountUsage: AccountUsage | null;
}> {
  try {
    const response = await http.get(cmsPagesEditorIndexPath());
    const mainNavigationPagesData = (response.data.mainNavigationPages || []) as RawPageFromApi[];
    const nonNavigationPagesData = (response.data.nonNavigationPages || []) as RawPageFromApi[];
    const publishedPages = (response.data.publishedPages || []) as RawPageFromApi[];
    const maxDepth = response.data.maxDepth;
    const maxPinnedPages = response.data.maxPinnedPages;
    const mainNavigationPages = normalizePages(mainNavigationPagesData);
    const nonNavigationPages = normalizePages(nonNavigationPagesData);
    const latestPagesUpdatedAt =
      typeof response.data.latestPagesUpdatedAt === 'string'
        ? response.data.latestPagesUpdatedAt
        : null;
    const accountUsage = (response.data.accountUsage as AccountUsage | undefined) ?? null;
    const indexPagesExist = normalizeIndexPagesExist(response.data.indexPagesExist);

    return {
      mainNavigationPages,
      nonNavigationPages,
      mainNavigationPagination:
        response.data.mainNavigationPagination ||
        createDefaultPaginationInfo({
          totalPages: 1,
          totalCount: mainNavigationPages.length,
          perPage: TREE_PAGE_DEFAULT_PER_PAGE,
        }),
      nonNavigationPagination:
        response.data.nonNavigationPagination ||
        createDefaultPaginationInfo({
          totalPages: 1,
          totalCount: nonNavigationPages.length,
          perPage: TREE_PAGE_DEFAULT_PER_PAGE,
        }),
      publishedPages: normalizePages(publishedPages),
      indexPagesExist,
      maxDepth,
      maxPinnedPages,
      latestPagesUpdatedAt,
      accountUsage,
    };
  } catch (error) {
    console.error('Failed to fetch pages:', error);
    return {
      mainNavigationPages: [],
      nonNavigationPages: [],
      mainNavigationPagination: createDefaultPaginationInfo({
        perPage: TREE_PAGE_DEFAULT_PER_PAGE,
      }),
      nonNavigationPagination: createDefaultPaginationInfo({
        perPage: TREE_PAGE_DEFAULT_PER_PAGE,
      }),
      publishedPages: [],
      indexPagesExist: EMPTY_INDEX_PAGES_EXIST,
      maxDepth: DEFAULT_MAX_TREE_DEPTH,
      maxPinnedPages: DEFAULT_MAX_PINNED_PAGES,
      latestPagesUpdatedAt: null,
      accountUsage: null,
    };
  }
}

export interface PaginatedPagesResponse {
  pages: Page[];
  pagination: PaginationInfo;
}

export function indexPageFromEditorPage(
  page: Pick<Page, 'indexPage'> | null | undefined,
  indexPageType: PageType,
): Page | undefined {
  const raw = page?.indexPage;
  if (!raw) return undefined;

  const normalized = raw.url != null ? raw : normalizePages([raw as unknown as RawPageFromApi])[0];

  return normalized?.pageType === indexPageType ? normalized : undefined;
}

export async function fetchTreePageBatch(options: {
  scope: 'navigation' | 'non_navigation';
  page?: number;
  perPage?: number;
}): Promise<PaginatedPagesResponse> {
  const response = await http.get(cmsPagesPageTreePagesPath(), {
    params: {
      scope: options.scope,
      page: options.page ?? DEFAULT_PAGE,
      perPage: options.perPage ?? TREE_PAGE_DEFAULT_PER_PAGE,
    },
  });

  const pagesData = (response.data.data || []) as RawPageFromApi[];
  const pagination =
    response.data.pagination ||
    createDefaultPaginationInfo({
      totalPages: 1,
      totalCount: pagesData.length,
      perPage: TREE_PAGE_DEFAULT_PER_PAGE,
    });

  return {
    pages: normalizePages(pagesData),
    pagination,
  };
}

export async function fetchLoadedTreeWindow(options: {
  scope: 'navigation' | 'non_navigation';
  loadedPageCount: number;
  fallbackPagination: PaginationInfo;
}): Promise<PaginatedPagesResponse> {
  const { scope, loadedPageCount, fallbackPagination } = options;
  const pagesToFetch = Array.from({ length: loadedPageCount }, (_, index) => index + 1);
  const batches = await Promise.all(
    pagesToFetch.map((page) => fetchTreePageBatch({ scope, page })),
  );

  const pages = batches.flatMap((batch) => batch.pages);
  const lastPagination = batches[batches.length - 1]?.pagination ?? fallbackPagination;
  const pagination: PaginationInfo = {
    ...lastPagination,
    currentPage: Math.min(loadedPageCount, Math.max(lastPagination.totalPages, 1)),
  };

  return { pages, pagination };
}

export async function fetchIndexPageChildren(
  pageType: PageType,
  page: number = DEFAULT_PAGE,
  perPage: number = DEFAULT_PER_PAGE,
  searchQuery: string = '',
): Promise<PaginatedPagesResponse> {
  const childPageType = INDEX_TO_CHILD_TYPE[pageType];
  const normalizedSearchQuery = searchQuery.trim();

  if (!childPageType) {
    return {
      pages: [],
      pagination: createDefaultPaginationInfo(),
    };
  }

  const response = await http.get(cmsPagesChildPageSearchesPath(), {
    params: {
      pageType: childPageType,
      page,
      perPage: perPage,
      ...(normalizedSearchQuery
        ? { q: { titleOrNavigationTitleICont: normalizedSearchQuery } }
        : {}),
    },
  });

  const pagesData = (response.data.data || []) as RawPageFromApi[];
  const pagination =
    response.data.pagination ||
    createDefaultPaginationInfo({
      totalPages: 1,
      totalCount: pagesData.length,
    });

  return {
    pages: normalizePages(pagesData),
    pagination,
  };
}

export async function fetchTreePages(searchQuery: string): Promise<{
  navigation: Page[];
  nonNavigation: Page[];
}> {
  try {
    const normalizedSearchQuery = searchQuery.trim();
    if (!normalizedSearchQuery) {
      return {
        navigation: [],
        nonNavigation: [],
      };
    }

    const response = await http.get(cmsPagesPageTreeSearchPath(), {
      params: {
        q: { titleOrNavigationTitleICont: normalizedSearchQuery },
      },
    });

    return {
      navigation: normalizePages((response.data.navigation || []) as RawPageFromApi[]),
      nonNavigation: normalizePages((response.data.nonNavigation || []) as RawPageFromApi[]),
    };
  } catch (error) {
    console.error('Failed to fetch tree pages:', error);
    return {
      navigation: [],
      nonNavigation: [],
    };
  }
}

export interface ReorderOperation {
  pageId: string;
  parentId: string | null;
  previousSiblingId: string | null;
}

export function isValidPinnedBoundaryDrop(
  flatItems: FlattenedItem<SortablePageItemData>[],
  targetIndex: number,
  isDraggedPinned: boolean,
): boolean {
  const prevItem = flatItems[targetIndex - 1];
  const nextItem = flatItems[targetIndex + 1];

  if (isDraggedPinned && prevItem && !prevItem.page.pinned) return false;
  if (!isDraggedPinned && nextItem && nextItem.page.pinned) return false;
  return true;
}

interface PagesToTreeItemsOptions {
  onOpenNestedView?: (page: Page) => void;
  depth?: number;
  maxDepth?: number;
  canPin?: boolean;
}

export function pagesToTreeItems(
  pages: Page[],
  {
    onOpenNestedView,
    depth = 0,
    maxDepth = getMaxTreeDepth(),
    canPin,
  }: PagesToTreeItemsOptions = {},
): TreeItems<SortablePageItemData> {
  const getSubtreeDepth = (item: { children?: unknown[] }): number =>
    item.children?.length
      ? 1 + Math.max(...item.children.map((c) => getSubtreeDepth(c as { children?: unknown[] })))
      : 0;

  return pages.map((page) => ({
    id: page.id,
    page,
    onOpenNestedView,
    canPin,
    children: page.children
      ? pagesToTreeItems(page.children, {
          onOpenNestedView,
          depth: depth + 1,
          maxDepth,
          canPin,
        })
      : undefined,
    canHaveChildren: (dragItem) => {
      return depth + 1 + getSubtreeDepth(dragItem) < maxDepth;
    },
  }));
}
